// Where things are on the grid: column positions from their widths, the
// selection (an anchor cell and the cell it was stretched to), cell names.
import { cellName, colName, parseCell } from '../xlsx/model'
import { GRID_COLS, GRID_ROWS } from './model'

export const ROW_H = 24
export const HEAD_H = 24
export const HEAD_W = 46
export const COL_W = 100

export { cellName, colName, parseCell }

/** Column lefts: xs[c] is where column c starts, xs[n] the end. */
export function colPositions(widths: Record<string, number>, n: number): Float64Array {
  const xs = new Float64Array(n + 1)
  for (let c = 0; c < n; c++) xs[c + 1] = xs[c]! + (widths[String(c)] ?? COL_W)
  return xs
}

/** The column at x (xs from colPositions); the last one past the end. */
export function colAt(xs: Float64Array, x: number): number {
  let lo = 0
  let hi = xs.length - 2
  if (hi < 0) return 0
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (xs[mid]! <= x) lo = mid
    else hi = mid - 1
  }
  return lo
}

export interface Sel {
  /** the active cell */
  ar: number
  ac: number
  /** the other corner */
  fr: number
  fc: number
}

export interface Rect {
  r1: number
  c1: number
  r2: number
  c2: number
}

export function rectOf(s: Sel): Rect {
  return { r1: Math.min(s.ar, s.fr), c1: Math.min(s.ac, s.fc), r2: Math.max(s.ar, s.fr), c2: Math.max(s.ac, s.fc) }
}

export const cellSel = (r: number, c: number): Sel => ({ ar: r, ac: c, fr: r, fc: c })

/** Whole columns / rows are selected (from a header). */
export function wholeCols(r: Rect): boolean {
  return r.r1 === 0 && r.r2 === GRID_ROWS - 1
}

export function wholeRows(r: Rect): boolean {
  return r.c1 === 0 && r.c2 === GRID_COLS - 1
}

/** "A1", "A1:C3", "B:B", "3:5" */
export function rectName(r: Rect): string {
  if (wholeCols(r)) return `${colName(r.c1)}:${colName(r.c2)}`
  if (wholeRows(r)) return `${r.r1 + 1}:${r.r2 + 1}`
  const a = cellName(r.r1, r.c1)
  return r.r1 === r.r2 && r.c1 === r.c2 ? a : `${a}:${cellName(r.r2, r.c2)}`
}

/** "A1:C3" (or "A1") to a rectangle; null when it is not one. */
export function parseRange(text: string): Rect | null {
  const [a = '', b] = text.trim().split(':')
  const p = parseCell(a)
  const q = b === undefined ? p : parseCell(b)
  if (!p || !q) return null
  return { r1: Math.min(p.row, q.row), c1: Math.min(p.col, q.col), r2: Math.max(p.row, q.row), c2: Math.max(p.col, q.col) }
}

/** A rectangle cut to the used part of a sheet (rows, cols: last used, -1 none). */
export function clampRect(r: Rect, used: { rows: number; cols: number }): Rect {
  return { r1: r.r1, c1: r.c1, r2: Math.min(r.r2, Math.max(r.r1, used.rows)), c2: Math.min(r.c2, Math.max(r.c1, used.cols)) }
}

/** What a click or tap on an area picks: a cell, a whole column or row, everything. */
export function areaSel(area: 'cell' | 'col' | 'row' | 'corner', r: number, c: number): Sel {
  if (area === 'col') return { ar: 0, ac: c, fr: GRID_ROWS - 1, fc: c }
  if (area === 'row') return { ar: r, ac: 0, fr: r, fc: GRID_COLS - 1 }
  if (area === 'corner') return { ar: 0, ac: 0, fr: GRID_ROWS - 1, fc: GRID_COLS - 1 }
  return cellSel(r, c)
}

/** Moves a cell by (dr, dc) inside the grid. */
export function move(r: number, c: number, dr: number, dc: number): { r: number; c: number } {
  return { r: Math.min(GRID_ROWS - 1, Math.max(0, r + dr)), c: Math.min(GRID_COLS - 1, Math.max(0, c + dc)) }
}
